import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import AdminLayout, { adminFetch, SectionTitle, AdminCard, Badge, useAdmin } from './AdminLayout';

const PLAN_COLORS = { discover: '#8A8A8A', essentiel: '#2980B9', complet: '#3D8B5E' };
const PLAN_LABELS = { discover: 'Discover (gratuit)', essentiel: 'Essentiel 29 €', complet: 'Complet 49 €' };

export default function AdminPaiements() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = () => {
    setLoading(true);
    setLoadError(false);
    adminFetch('/api/admin/paiements')
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setData)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <AdminLayout title="Paiements & Revenue">
      <Content data={data} loading={loading} loadError={loadError} onRetry={load} />
    </AdminLayout>
  );
}

function Content({ data, loading, loadError, onRetry }) {
  const { dark, cardBg, border } = useAdmin();
  const textColor  = dark ? '#e0e0e0' : '#2D2D2D';
  const mutedColor = '#8A8A8A';

  if (loading) return <p style={{ color: mutedColor, padding: 16 }}>Chargement…</p>;
  if (loadError || !data) {
    return (
      <div style={{
        padding: 16, borderRadius: 10, background: '#FDECEA',
        border: '1px solid rgba(192,57,43,0.3)', color: '#933',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <span>Erreur de chargement. Vérifiez votre connexion.</span>
        <button onClick={onRetry} style={{
          padding: '8px 16px', borderRadius: 8, border: 'none',
          background: '#D4724A', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Réessayer</button>
      </div>
    );
  }

  const { mrr, arr, last_payments, plans, monthly_revenue } = data;

  const plansData = Object.entries(plans)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: PLAN_LABELS[k] || k, value: v, color: PLAN_COLORS[k] || '#D4724A' }));

  const allZero = last_payments.length === 0;

  return (
    <div>
      {/* MRR / ARR */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={{
          background: 'linear-gradient(135deg, #D4724A, #F0B89A)',
          borderRadius: 16, padding: '28px', gridColumn: 'span 2',
          boxShadow: '0 4px 16px rgba(212,114,74,0.25)',
        }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            MRR — Revenu mensuel récurrent
          </div>
          <div style={{ fontSize: 40, fontWeight: 700, color: '#fff', fontFamily: 'Lora, serif' }}>
            {parseFloat(mrr).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #3D8B5E, #5C8C6E)',
          borderRadius: 16, padding: '28px', gridColumn: 'span 2',
          boxShadow: '0 4px 16px rgba(61,139,94,0.25)',
        }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            ARR — Revenu annuel récurrent
          </div>
          <div style={{ fontSize: 40, fontWeight: 700, color: '#fff', fontFamily: 'Lora, serif' }}>
            {parseFloat(arr).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 24 }}>
        {/* Graphique revenus mensuels */}
        <AdminCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <SectionTitle>Revenus mensuels (6 mois)</SectionTitle>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthly_revenue} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={dark ? '#2d2d4e' : '#F0EDE8'} />
              <XAxis dataKey="mois" tick={{ fontSize: 11, fill: mutedColor }} tickFormatter={m => m.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: mutedColor }} unit=" €" />
              <Tooltip
                contentStyle={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 8, fontSize: 13 }}
                formatter={v => [`${v} €`, 'Revenue']}
              />
              <Bar dataKey="revenue" fill="#D4724A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          {monthly_revenue.every(m => m.revenue === 0) && (
            <p style={{ textAlign: 'center', fontSize: 13, color: mutedColor, marginTop: 8 }}>
              Les revenus s'afficheront dès les premiers paiements.
            </p>
          )}
        </AdminCard>

        {/* Distribution des plans */}
        <AdminCard>
          <SectionTitle>Distribution des plans</SectionTitle>
          {plansData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>💳</div>
              <p style={{ fontSize: 13, color: mutedColor }}>Aucun abonnement actif pour l'instant.</p>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={plansData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                    {plansData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {plansData.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                    <span style={{ color: textColor, flex: 1 }}>{p.name}</span>
                    <span style={{ color: p.color, fontWeight: 600 }}>{p.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </AdminCard>
      </div>

      {/* Tableau paiements */}
      <AdminCard style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <SectionTitle>Paiements — 30 derniers jours</SectionTitle>
          <a
            href="https://dashboard.stripe.com/payments"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: '#635BFF', color: '#fff', textDecoration: 'none',
            }}
          >
            Ouvrir Stripe Dashboard ↗
          </a>
        </div>

        {allZero ? (
          <p style={{ fontSize: 14, color: mutedColor }}>Aucun paiement dans les 30 derniers jours.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${border}` }}>
                {['Date', 'Plan', 'Montant', 'Statut', 'ID Stripe'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: mutedColor, fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {last_payments.map((p, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${border}` }}>
                  <td style={{ padding: '10px', color: textColor, whiteSpace: 'nowrap' }}>
                    {p.ts ? new Date(p.ts).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td style={{ padding: '10px' }}>
                    <Badge color={PLAN_COLORS[p.plan] || '#D4724A'}>
                      {PLAN_LABELS[p.plan] || p.plan || '—'}
                    </Badge>
                  </td>
                  <td style={{ padding: '10px', fontWeight: 700, color: '#3D8B5E' }}>
                    {p.amount ? `${(p.amount / 100).toFixed(2)} €` : '—'}
                  </td>
                  <td style={{ padding: '10px' }}>
                    <Badge color={p.status === 'succeeded' ? '#3D8B5E' : '#C0392B'}>
                      {p.status === 'succeeded' ? 'Réussi' : p.status || '—'}
                    </Badge>
                  </td>
                  <td style={{ padding: '10px', color: mutedColor, fontSize: 11, fontFamily: 'monospace' }}>
                    {p.id || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </AdminCard>
    </div>
  );
}
